/**
 * SPEC-20260507-2200: aba "Variáveis" do TenantDetail.
 *
 * Mostra:
 * - Seção read-only "Builtins disponíveis" (pra user saber o que pode usar)
 * - Drill-down por sagaType — cada saga tem seu form com placeholders/secrets
 * - Inputs por type, sensitivity toggle, olhinho pra revelar secrets
 * - Botão "Auto-preencher via IA" pra placeholders não-sensitive
 */

import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Lock,
  Sparkles,
  Loader2,
  Save,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  listTenantVariables,
  upsertTenantSagaVariables,
  deleteTenantSagaVariable,
  setTenantSagaVariableSensitivity,
  revealTenantSagaVariable,
  autoFillTenantSagaVariables,
  listBuiltins,
  getPlaceholderRequirements,
  type SagaVariableView,
  type SagaVariableValueType,
  type PlaceholderRequirement,
} from "../../services/saga-variables";
import { listSagaDefinitions } from "../../services/saga-definition";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { cn } from "../../utils/cn";

interface Props {
  tenantKey: string;
}

interface SagaSection {
  sagaType: string;
  requirements: PlaceholderRequirement[];
  /** name → value working state (com edições não salvas) */
  workingValues: Record<string, string>;
  /** name → isSensitive override pelo user (se ele toggle) */
  sensitivityOverrides: Record<string, boolean>;
  expanded: boolean;
}

export function TenantSagaVariablesTab({ tenantKey }: Props) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [builtins, setBuiltins] = useState<Array<{ name: string; description: string }>>([]);
  const [sections, setSections] = useState<SagaSection[]>([]);
  /** Quais valores secrets foram revelados (key = `${sagaType}:${name}`). Map → string. */
  const [revealed, setRevealed] = useState<Map<string, string>>(new Map());
  const [savingSaga, setSavingSaga] = useState<string | null>(null);
  const [autoFillingSaga, setAutoFillingSaga] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const [defs, b] = await Promise.all([listSagaDefinitions({ active: true }), listBuiltins()]);
      setBuiltins(b);
      // Pra cada saga active, busca placeholder-requirements (com hasValue/source preenchidos pro tenant).
      const reqsList = await Promise.all(
        defs.map(async (d) => {
          try {
            const r = await getPlaceholderRequirements(d.name, tenantKey);
            return { sagaType: d.name, required: r.required };
          } catch {
            return { sagaType: d.name, required: [] };
          }
        }),
      );
      // Pra pré-popular workingValues, lê variáveis existentes do tenant.
      const tenantData = await listTenantVariables(tenantKey);
      const existingByGroup = new Map<string, SagaVariableView[]>();
      // SPEC-2300: groups vêm com (sagaType, stepId) — esta tab cuida só do escopo (tenant, saga, null).
      for (const g of tenantData.groups) {
        if (g.sagaType && g.stepId === null) existingByGroup.set(g.sagaType, g.variables);
      }

      const newSections: SagaSection[] = reqsList
        .filter((r) => r.required.length > 0)
        .map(({ sagaType, required }) => {
          const existing = existingByGroup.get(sagaType) ?? [];
          const workingValues: Record<string, string> = {};
          const sensitivityOverrides: Record<string, boolean> = {};
          for (const v of existing) {
            // Deixa em branco — sensitive permanece "•••••" até user revelar e editar.
            if (!v.isSensitive) workingValues[v.name] = v.value;
            sensitivityOverrides[v.name] = v.isSensitive;
          }
          // Defaults pra placeholders sem valor.
          for (const r of required) {
            if (!(r.name in workingValues) && r.default !== undefined) {
              workingValues[r.name] = r.default;
            }
          }
          return {
            sagaType,
            requirements: required,
            workingValues,
            sensitivityOverrides,
            expanded: false,
          };
        });
      setSections(newSections);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao carregar variáveis");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantKey]);

  function toggleExpanded(sagaType: string) {
    setSections((prev) => prev.map((s) => (s.sagaType === sagaType ? { ...s, expanded: !s.expanded } : s)));
  }

  function setWorkingValue(sagaType: string, name: string, value: string) {
    setSections((prev) =>
      prev.map((s) =>
        s.sagaType === sagaType
          ? { ...s, workingValues: { ...s.workingValues, [name]: value } }
          : s,
      ),
    );
  }

  function setOverrideSensitivity(sagaType: string, name: string, isSensitive: boolean) {
    setSections((prev) =>
      prev.map((s) =>
        s.sagaType === sagaType
          ? {
              ...s,
              sensitivityOverrides: { ...s.sensitivityOverrides, [name]: isSensitive },
            }
          : s,
      ),
    );
  }

  async function handleReveal(sagaType: string, name: string) {
    const key = `${sagaType}:${name}`;
    if (revealed.has(key)) {
      // Esconde
      const next = new Map(revealed);
      next.delete(key);
      setRevealed(next);
      return;
    }
    try {
      const value = await revealTenantSagaVariable(tenantKey, sagaType, name);
      setRevealed((prev) => new Map(prev).set(key, value));
      // Pré-popula working pra user editar se quiser.
      setWorkingValue(sagaType, name, value);
      // Auto-esconde em 30s.
      setTimeout(() => {
        setRevealed((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      }, 30_000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao revelar");
    }
  }

  async function handleToggleSensitivity(sagaType: string, name: string, current: boolean) {
    try {
      await setTenantSagaVariableSensitivity(tenantKey, sagaType, name, !current);
      setOverrideSensitivity(sagaType, name, !current);
      toast.success(`'${name}' agora é ${!current ? "sensitive" : "não-sensitive"}`);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  async function handleSave(sagaType: string) {
    const section = sections.find((s) => s.sagaType === sagaType);
    if (!section) return;
    setSavingSaga(sagaType);
    try {
      const variables: Record<string, { valueType: SagaVariableValueType; value: string; isSensitive?: boolean; choices?: string[] }> = {};
      for (const r of section.requirements) {
        const value = section.workingValues[r.name];
        if (value === undefined || value === "") continue; // pula vazios
        variables[r.name] = {
          valueType: r.type,
          value,
          isSensitive: section.sensitivityOverrides[r.name] ?? r.isSensitive,
          ...(r.choices ? { choices: r.choices } : {}),
        };
      }
      if (Object.keys(variables).length === 0) {
        toast.warning("Nenhum valor pra salvar");
        return;
      }
      await upsertTenantSagaVariables(tenantKey, sagaType, variables);
      toast.success(`Variáveis salvas em ${sagaType}`);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setSavingSaga(null);
    }
  }

  async function handleDelete(sagaType: string, name: string) {
    if (!confirm(`Remover variável '${name}' de ${sagaType}?`)) return;
    try {
      await deleteTenantSagaVariable(tenantKey, sagaType, name);
      toast.success(`Removida ${name}`);
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  async function handleAutoFill(sagaType: string) {
    setAutoFillingSaga(sagaType);
    try {
      const suggestions = await autoFillTenantSagaVariables(tenantKey, sagaType, true);
      const filled = suggestions.filter((s) => s.suggestedValue !== null);
      setSections((prev) =>
        prev.map((s) => {
          if (s.sagaType !== sagaType) return s;
          const newValues = { ...s.workingValues };
          for (const sug of filled) {
            if (sug.suggestedValue !== null) newValues[sug.name] = sug.suggestedValue;
          }
          return { ...s, workingValues: newValues };
        }),
      );
      toast.success(
        `IA preencheu ${filled.length} valor(es). Secrets ficam em branco — preencha manualmente.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro auto-fill");
    } finally {
      setAutoFillingSaga(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
        <Loader2 size={14} className="animate-spin" /> carregando variáveis…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded border border-danger/30 bg-danger-soft/40 p-3 text-sm text-danger-soft-fg">
          {err}
        </div>
      )}

      {/* Seção builtins read-only */}
      <details className="rounded border border-border bg-surface-1 p-3">
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Zap size={12} /> Builtins disponíveis (resolvidos automaticamente)
        </summary>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-[11px]">
          {builtins.map((b) => (
            <div key={b.name} className="flex gap-2">
              <code className="font-mono-tight text-primary-soft-fg shrink-0">{`{{${b.name}}}`}</code>
              <span className="text-muted-foreground">— {b.description}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Sagas */}
      {sections.length === 0 ? (
        <div className="text-sm text-muted-foreground italic text-center py-6">
          Nenhuma saga active tem placeholders requeridos.
        </div>
      ) : (
        sections.map((section) => (
          <SagaCard
            key={section.sagaType}
            section={section}
            onToggleExpand={() => toggleExpanded(section.sagaType)}
            onSetValue={(name, v) => setWorkingValue(section.sagaType, name, v)}
            onReveal={(name) => handleReveal(section.sagaType, name)}
            onToggleSensitivity={(name, curr) => handleToggleSensitivity(section.sagaType, name, curr)}
            onDelete={(name) => handleDelete(section.sagaType, name)}
            onSave={() => handleSave(section.sagaType)}
            onAutoFill={() => handleAutoFill(section.sagaType)}
            saving={savingSaga === section.sagaType}
            autoFilling={autoFillingSaga === section.sagaType}
            revealed={revealed}
          />
        ))
      )}
    </div>
  );
}

interface SagaCardProps {
  section: SagaSection;
  onToggleExpand(): void;
  onSetValue(name: string, v: string): void;
  onReveal(name: string): void;
  onToggleSensitivity(name: string, current: boolean): void;
  onDelete(name: string): void;
  onSave(): void;
  onAutoFill(): void;
  saving: boolean;
  autoFilling: boolean;
  revealed: Map<string, string>;
}

function SagaCard({
  section,
  onToggleExpand,
  onSetValue,
  onReveal,
  onToggleSensitivity,
  onDelete,
  onSave,
  onAutoFill,
  saving,
  autoFilling,
  revealed,
}: SagaCardProps) {
  const stats = useMemo(() => {
    const total = section.requirements.length;
    const defined = section.requirements.filter((r) => r.hasValue).length;
    return { total, defined };
  }, [section.requirements]);

  return (
    <div className="rounded border border-border bg-surface-1">
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-surface-2 transition-colors"
      >
        <div className="flex items-center gap-2">
          {section.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-medium text-sm">{section.sagaType}</span>
          <span className="text-xs text-muted-foreground">
            {stats.defined}/{stats.total} variáveis configuradas
          </span>
        </div>
      </button>

      {section.expanded && (
        <div className="border-t border-border p-3 space-y-3">
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onAutoFill}
              disabled={autoFilling || saving}
            >
              {autoFilling ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Auto-preencher via IA
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving || autoFilling}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Salvar
            </Button>
          </div>

          <div className="space-y-2.5">
            {section.requirements.map((req) => {
              const isSensitive = section.sensitivityOverrides[req.name] ?? req.isSensitive;
              const revealKey = `${section.sagaType}:${req.name}`;
              const isRevealed = revealed.has(revealKey);
              const currentValue = section.workingValues[req.name] ?? "";
              return (
                <VariableRow
                  key={req.name}
                  req={req}
                  isSensitive={isSensitive}
                  isRevealed={isRevealed}
                  value={currentValue}
                  onSetValue={(v) => onSetValue(req.name, v)}
                  onReveal={() => onReveal(req.name)}
                  onToggleSensitivity={() => onToggleSensitivity(req.name, isSensitive)}
                  onDelete={() => onDelete(req.name)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

interface VariableRowProps {
  req: PlaceholderRequirement;
  isSensitive: boolean;
  isRevealed: boolean;
  value: string;
  onSetValue(v: string): void;
  onReveal(): void;
  onToggleSensitivity(): void;
  onDelete(): void;
}

function VariableRow({
  req,
  isSensitive,
  isRevealed,
  value,
  onSetValue,
  onReveal,
  onToggleSensitivity,
  onDelete,
}: VariableRowProps) {
  return (
    <div
      className={cn(
        "rounded border p-2.5",
        isSensitive ? "border-danger/30 bg-danger-soft/10" : "border-border bg-bg",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <Label htmlFor={`var-${req.name}`} className="flex items-center gap-1.5 text-xs font-medium">
            {isSensitive && <Lock size={11} className="text-danger" />}
            <code className="font-mono-tight">{req.name}</code>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{req.type}</span>
            {req.hasValue && req.source && (
              <span
                className={cn(
                  "text-[10px] uppercase tracking-wider px-1 py-0.5 rounded",
                  req.source === "tenant" && "bg-success-soft text-success-soft-fg",
                  req.source === "global" && "bg-info-soft text-info-soft-fg",
                  req.source === "default" && "bg-warning-soft text-warning-soft-fg",
                )}
              >
                {req.source}
              </span>
            )}
            {!req.hasValue && (
              <span className="text-[10px] uppercase tracking-wider px-1 py-0.5 rounded bg-danger-soft text-danger-soft-fg">
                missing
              </span>
            )}
          </Label>
          {req.description && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{req.description}</p>
          )}
          <div className="mt-1.5 flex gap-1.5 items-stretch">
            {req.type === "choice" && req.choices ? (
              <select
                id={`var-${req.name}`}
                value={value}
                onChange={(e) => onSetValue(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs border border-border rounded bg-bg"
              >
                <option value="">— selecione —</option>
                {req.choices.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : req.type === "boolean" ? (
              <select
                id={`var-${req.name}`}
                value={value}
                onChange={(e) => onSetValue(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs border border-border rounded bg-bg"
              >
                <option value="">— selecione —</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <Input
                id={`var-${req.name}`}
                type={
                  isSensitive && !isRevealed
                    ? "password"
                    : req.type === "number"
                      ? "number"
                      : req.type === "date"
                        ? "date"
                        : req.type === "datetime"
                          ? "datetime-local"
                          : "text"
                }
                value={value}
                onChange={(e) => onSetValue(e.target.value)}
                placeholder={
                  isSensitive && !value
                    ? "(secret existente — clique no olho pra revelar)"
                    : req.default
                      ? `default: ${req.default}`
                      : ""
                }
                className="flex-1"
              />
            )}
            {isSensitive && (
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={onReveal}
                title={isRevealed ? "Esconder" : "Revelar (auto-esconde em 30s)"}
              >
                {isRevealed ? <EyeOff size={12} /> : <Eye size={12} />}
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button
            size="sm"
            variant="outline"
            type="button"
            onClick={onToggleSensitivity}
            title={isSensitive ? "Mover pra não-sensitive" : "Marcar como sensitive (encrypt)"}
            className="text-[10px] px-1.5"
          >
            {isSensitive ? "→ plain" : "→ secret"}
          </Button>
          {req.hasValue && (
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={onDelete}
              title="Remover variável"
            >
              <Trash2 size={11} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
